import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Eye } from "lucide-react";
import { getFormattedDateTime } from "@/utils";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import {
	ScratchCardApi,
	SpinAWheelApi,
	GetScratchCardReportByIdRequest,
	ScratchCardReportByIdRow,
	GetScratchReportRequest,
	ScratchReportDataRow,
	GetSpinAwheelReportRequest,
	SpinAwheelReportDataRow,
	GetSpinAwheelReportByIdRequest,
	SpinAwheelReportByIdRow,
} from "@/api/giftcard.api";

type ReportType = "spin" | "scratch";

interface ReportRow {
	id: number;
	name: string; // Spin a Wheel Name / Scratch Card Name
	attempted: number; // No of users attempted
	usersWon: number; // Users won the rewards
	redeemed: number; // Redeemed rewards
	businessGenerated: number; // Business Generated (amount)
	details: DetailRow[];
}

interface DetailRow {
	spinName: string;
	userName: string;
	reward: string;
	redeemed: boolean;
	businessGenerated: number;
	issuedDate: string; // ISO
	redeemDate?: string; // ISO or undefined
}

export function GamificationReport() {
	const [reportType, setReportType] = useState<ReportType>("spin");
	const [selectedRow, setSelectedRow] = useState<ReportRow | null>(null);
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const { toast } = useToast();
	const primaryNameLabel =
		reportType === "spin" ? "Spin a Wheel Name" : "Scratch Card Name";

	const spinMockData: ReportRow[] = useMemo(() => {
		return [
				{
					id: 1,
					name: "Diwali Mega Spin",
					attempted: 340,
					usersWon: 220,
					redeemed: 180,
					businessGenerated: 245000,
					details: [
						{
							spinName: "Diwali Mega Spin",
							userName: "Aarav Patel",
							reward: "200 Points",
							redeemed: true,
							businessGenerated: 1200,
							issuedDate: "2025-10-28T10:12:00Z",
							redeemDate: "2025-10-30T08:45:00Z",
						},
						{
							spinName: "Diwali Mega Spin",
							userName: "Isha Sharma",
							reward: "50 Points",
							redeemed: false,
							businessGenerated: 0,
							issuedDate: "2025-10-28T11:05:00Z",
							redeemDate: undefined,
						},
					],
				},
				{
					id: 2,
					name: "Weekend Bonanza",
					attempted: 190,
					usersWon: 120,
					redeemed: 95,
					businessGenerated: 96000,
					details: [
						{
							spinName: "Weekend Bonanza",
							userName: "Rahul Verma",
							reward: "Free Coffee",
							redeemed: true,
							businessGenerated: 350,
							issuedDate: "2025-11-15T09:30:00Z",
							redeemDate: "2025-11-15T10:00:00Z",
						},
					],
				},
			];
	}, []);

	const [tableData, setTableData] = useState<ReportRow[]>(spinMockData);

	// Scratch report pagination
	const [scratchPage, setScratchPage] = useState<number>(0); // 0-based
	const [scratchPageSize, setScratchPageSize] = useState<number>(10);
	const [scratchTotalRecords, setScratchTotalRecords] = useState<number>(0);
	const [isScratchLoading, setIsScratchLoading] = useState<boolean>(false);

	// Scratch card details pagination (modal)
	const [scratchDetails, setScratchDetails] = useState<ScratchCardReportByIdRow[]>(
		[]
	);
	const [isScratchDetailsLoading, setIsScratchDetailsLoading] =
		useState<boolean>(false);
	const [scratchDetailsPage, setScratchDetailsPage] = useState<number>(0); // 0-based
	const [scratchDetailsPageSize, setScratchDetailsPageSize] = useState<number>(10);
	const [scratchDetailsTotalRecords, setScratchDetailsTotalRecords] =
		useState<number>(0);

	// Spin card details pagination (modal)
	const [spinDetails, setSpinDetails] = useState<SpinAwheelReportByIdRow[]>(
		[]
	);
	const [isSpinDetailsLoading, setIsSpinDetailsLoading] =
		useState<boolean>(false);
	const [spinDetailsPage, setSpinDetailsPage] = useState<number>(0); // 0-based
	const [spinDetailsPageSize, setSpinDetailsPageSize] =
		useState<number>(10);
	const [spinDetailsTotalRecords, setSpinDetailsTotalRecords] =
		useState<number>(0);

	// Client id is fixed as per your curl example; once you have logged-in clientId, we can swap it.
	const scratchClientId = 1009;

	// Spin report pagination
	const [spinPage, setSpinPage] = useState<number>(0); // 0-based
	const [spinPageSize, setSpinPageSize] = useState<number>(10);
	const [spinTotalRecords, setSpinTotalRecords] = useState<number>(0);
	const [isSpinLoading, setIsSpinLoading] = useState<boolean>(false);

	const formatCurrency = (amount: number) =>
		new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			maximumFractionDigits: 0,
		}).format(amount);

	// Ensure modal is closed when switching report type
	useEffect(() => {
		setSelectedRow(null);
		setIsDetailsOpen(false);

		if (reportType === "spin") {
			setSpinPage(0);
			setSpinTotalRecords(0);
			setTableData([]);
			setSpinDetails([]);
			setIsSpinDetailsLoading(false);
			setSpinDetailsPage(0);
			setSpinDetailsPageSize(10);
			setSpinDetailsTotalRecords(0);
		}
		if (reportType === "scratch") {
			setScratchPage(0);
			setTableData([]);
			setScratchTotalRecords(0);
			setScratchDetails([]);
			setIsScratchDetailsLoading(false);
			setScratchDetailsPage(0);
			setScratchDetailsPageSize(10);
			setScratchDetailsTotalRecords(0);
		}
	}, [reportType, spinMockData]);

	useEffect(() => {
		if (reportType !== "spin") return;

		const fetchSpinReport = async () => {
			setIsSpinLoading(true);
			try {
				const payload: GetSpinAwheelReportRequest = {
					p_clientId: scratchClientId,
					p_PageNumber: spinPage + 1,
					p_PageSize: spinPageSize,
				};

				const response = await SpinAWheelApi.getSpinAwheelReportData(
					payload
				);

				if (response.error_msg && response.error_msg.trim() !== "") {
					toast({
						title: "Warning",
						description: response.error_msg,
						variant: "destructive",
					});
				}

				const apiRows: SpinAwheelReportDataRow[] =
					response.getSpinAwheelReport ?? [];

				setTableData(
					apiRows.map((r) => ({
						id: r.spinId,
						name: r.spinName,
						attempted: r.noOfUsersAttempted,
						usersWon: r.usersWonReward,
						redeemed: r.redeemedRewards,
						businessGenerated: r.businessGenerated,
						details: [],
					}))
				);
				setSpinTotalRecords(response.total_records ?? 0);
			} catch (error) {
				console.error("Error fetching spin report:", error);
				const errorMessage = HandleError(error);
				toast({
					title: "Error",
					description: errorMessage || "Failed to fetch spin report",
					variant: "destructive",
				});
				setTableData([]);
				setSpinTotalRecords(0);
			} finally {
				setIsSpinLoading(false);
			}
		};

		fetchSpinReport();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reportType, spinPage, spinPageSize]);

	useEffect(() => {
		if (reportType !== "scratch") return;

		const fetchScratchReport = async () => {
			setIsScratchLoading(true);
			try {
				const payload: GetScratchReportRequest = {
					p_clientId: scratchClientId,
					p_PageNumber: scratchPage + 1,
					p_PageSize: scratchPageSize,
				};

				const response = await ScratchCardApi.getScratchReportData(payload);

				if (response.error_msg && response.error_msg.trim() !== "") {
					toast({
						title: "Warning",
						description: response.error_msg,
						variant: "destructive",
					});
				}

				const apiRows: ScratchReportDataRow[] = response.scratchReportData ?? [];
				setTableData(
					apiRows.map((r) => ({
						id: r.scratchCardId,
						name: r.scratchCardName,
						attempted: r.noOfUsersAttempted,
						usersWon: r.usersWonReward,
						redeemed: r.redeemedRewards,
						businessGenerated: r.businessGenerated,
						// User-level detail endpoint not provided in this request yet.
						details: [],
					}))
				);
				setScratchTotalRecords(response.total_records ?? 0);
			} catch (error) {
				console.error("Error fetching scratch report:", error);
				const errorMessage = HandleError(error);
				toast({
					title: "Error",
					description: errorMessage || "Failed to fetch scratch report",
					variant: "destructive",
				});
				setTableData([]);
				setScratchTotalRecords(0);
			} finally {
				setIsScratchLoading(false);
			}
		};

		fetchScratchReport();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reportType, scratchPage, scratchPageSize]);

	useEffect(() => {
		if (reportType !== "scratch") return;
		if (!isDetailsOpen) return;
		if (!selectedRow) return;

		const fetchScratchCardDetails = async () => {
			const scratchCardId = selectedRow.id;
			if (!scratchCardId) return;

			setIsScratchDetailsLoading(true);
			try {
				const payload: GetScratchCardReportByIdRequest = {
					p_scratchCardId: scratchCardId,
					p_PageNumber: scratchDetailsPage + 1,
					p_PageSize: scratchDetailsPageSize,
				};

				const response = await ScratchCardApi.getScratchCardReportById(
					payload
				);

				if (
					response.error_msg &&
					response.error_msg.trim() !== ""
				) {
					toast({
						title: "Warning",
						description: response.error_msg,
						variant: "destructive",
					});
				}

				setScratchDetails(response.getScratchCardReportById ?? []);
				setScratchDetailsTotalRecords(response.total_records ?? 0);
			} catch (error) {
				console.error("Error fetching scratch card details:", error);
				const errorMessage = HandleError(error);
				toast({
					title: "Error",
					description:
						errorMessage || "Failed to fetch scratch card details",
					variant: "destructive",
				});
				setScratchDetails([]);
				setScratchDetailsTotalRecords(0);
			} finally {
				setIsScratchDetailsLoading(false);
			}
		};

		fetchScratchCardDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		reportType,
		isDetailsOpen,
		selectedRow,
		scratchDetailsPage,
		scratchDetailsPageSize,
	]);

	useEffect(() => {
		if (reportType !== "spin") return;
		if (!isDetailsOpen) return;
		if (!selectedRow) return;

		const fetchSpinCardDetails = async () => {
			const spinCardId = selectedRow.id;
			if (!spinCardId) return;

			setIsSpinDetailsLoading(true);
			try {
				const payload: GetSpinAwheelReportByIdRequest = {
					p_spinId: spinCardId,
					p_PageNumber: spinDetailsPage + 1,
					p_PageSize: spinDetailsPageSize,
				};

				const response = await SpinAWheelApi.getSpinAwheelReportById(
					payload
				);

				if (response.error_msg && response.error_msg.trim() !== "") {
					toast({
						title: "Warning",
						description: response.error_msg,
						variant: "destructive",
					});
				}

				setSpinDetails(response.getSpinAwheelReportById ?? []);
				setSpinDetailsTotalRecords(response.total_records ?? 0);
			} catch (error) {
				console.error("Error fetching spin card details:", error);
				const errorMessage = HandleError(error);
				toast({
					title: "Error",
					description:
						errorMessage || "Failed to fetch spin card details",
					variant: "destructive",
				});
				setSpinDetails([]);
				setSpinDetailsTotalRecords(0);
			} finally {
				setIsSpinDetailsLoading(false);
			}
		};

		fetchSpinCardDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		reportType,
		isDetailsOpen,
		selectedRow,
		spinDetailsPage,
		spinDetailsPageSize,
	]);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-medium text-gray-800">Report</h2>
					<p className="text-sm text-muted-foreground">
						View engagement, redemption, and business generated
					</p>
				</div>
				<div className="min-w-[220px]">
					<Select
						value={reportType}
						onValueChange={(v) => setReportType(v as ReportType)}
					>
						<SelectTrigger>
							<SelectValue placeholder="Select type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="scratch">Scratch Card</SelectItem>
							<SelectItem value="spin">Spin the Wheel</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			{(reportType === "spin" && isSpinLoading) ||
			(reportType === "scratch" && isScratchLoading) ? (
				<CouponTableSkeleton rowCount={5} />
			) : (
				<DataTable
					title=""
					data={tableData}
					cellClassName="py-6 text-gray-700"
					columns={[
						{
							header: primaryNameLabel,
							accessorKey: "name" as keyof ReportRow,
						},
						{
							header: "No of users attempted",
							accessorKey: "attempted" as keyof ReportRow,
							cell: (row: ReportRow) => row.attempted.toString(),
						},
						{
							header: "Users won the rewards",
							accessorKey: "usersWon" as keyof ReportRow,
							cell: (row: ReportRow) => row.usersWon.toString(),
						},
						{
							header: "Redeemed rewards",
							accessorKey: "redeemed" as keyof ReportRow,
							cell: (row: ReportRow) => row.redeemed.toString(),
						},
						{
							header: "Business Generated",
							accessorKey: "businessGenerated" as keyof ReportRow,
							cell: (row: ReportRow) =>
								formatCurrency(row.businessGenerated),
						},
						{
							header: "Action",
							id: "action",
							cell: (row: ReportRow) => (
								<Button
									variant="outline"
									size="sm"
									className="flex items-center gap-2"
									onClick={() => {
										setSelectedRow(row);
										setIsDetailsOpen(true);
										if (reportType === "scratch") {
											setScratchDetailsPage(0);
											setScratchDetails([]);
											setScratchDetailsTotalRecords(0);
										}
										if (reportType === "spin") {
											setSpinDetailsPage(0);
											setSpinDetails([]);
											setSpinDetailsPageSize(10);
											setSpinDetailsTotalRecords(0);
										}
									}}
								>
									<Eye className="h-4 w-4" />
									View Details
								</Button>
							),
						},
					]}
				/>
			)}

			{reportType === "scratch" && !isScratchLoading && scratchTotalRecords > 0 && (
				<div className="mt-4 flex justify-between items-center">
					<div className="flex items-center space-x-2">
						<span className="text-sm text-gray-700">Rows per page:</span>
						<Select
							value={scratchPageSize.toString()}
							onValueChange={(value) => {
								const newPageSize = parseInt(value, 10);
								setScratchPageSize(newPageSize);
								setScratchPage(0);
							}}
						>
							<SelectTrigger className="w-20">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="5">5</SelectItem>
								<SelectItem value="10">10</SelectItem>
								<SelectItem value="20">20</SelectItem>
								<SelectItem value="30">30</SelectItem>
								<SelectItem value="50">50</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{Math.ceil(scratchTotalRecords / scratchPageSize) > 1 && (
						<PaginationControls
							currentPage={scratchPage}
							totalPages={Math.ceil(scratchTotalRecords / scratchPageSize)}
							onPrevHandler={() => setScratchPage((p) => Math.max(0, p - 1))}
							onNextHandler={() =>
								setScratchPage((p) =>
									Math.min(
										Math.ceil(scratchTotalRecords / scratchPageSize) - 1,
										p + 1
									)
								)
							}
							onPageChange={(page) => setScratchPage(page)}
						/>
					)}
				</div>
			)}

			{reportType === "spin" &&
				!isSpinLoading &&
				spinTotalRecords > 0 && (
					<div className="mt-4 flex justify-between items-center">
						<div className="flex items-center space-x-2">
							<span className="text-sm text-gray-700">
								Rows per page:
							</span>
							<Select
								value={spinPageSize.toString()}
								onValueChange={(value) => {
									const newPageSize = parseInt(value, 10);
									setSpinPageSize(newPageSize);
									setSpinPage(0);
								}}
							>
								<SelectTrigger className="w-20">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="5">5</SelectItem>
									<SelectItem value="10">10</SelectItem>
									<SelectItem value="20">20</SelectItem>
									<SelectItem value="30">30</SelectItem>
									<SelectItem value="50">50</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{Math.ceil(spinTotalRecords / spinPageSize) > 1 && (
							<PaginationControls
								currentPage={spinPage}
								totalPages={Math.ceil(spinTotalRecords / spinPageSize)}
								onPrevHandler={() =>
									setSpinPage((p) => Math.max(0, p - 1))
								}
								onNextHandler={() =>
									setSpinPage((p) =>
										Math.min(
											Math.ceil(spinTotalRecords / spinPageSize) - 1,
											p + 1
										)
									)
								}
								onPageChange={(page) => setSpinPage(page)}
							/>
						)}
					</div>
				)}

			<Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
				<DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{reportType === "spin" ? "Spin a Wheel" : "Scratch Card"} Details
						</DialogTitle>
					</DialogHeader>

					{selectedRow &&
						(reportType === "scratch" ? (
							<div className="space-y-4 py-2">
								{isScratchDetailsLoading ? (
									<CouponTableSkeleton rowCount={5} />
								) : scratchDetails.length === 0 ? (
									<div className="text-sm text-gray-500 py-4">
										No detail records available for this report.
									</div>
								) : (
									<>
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>{primaryNameLabel}</TableHead>
													<TableHead>User name</TableHead>
													<TableHead>Rewards</TableHead>
													<TableHead>Redeemed</TableHead>
													<TableHead>Business Generated</TableHead>
													<TableHead>IssuedDate</TableHead>
													<TableHead>RedeemDate</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{scratchDetails.map((d, idx) => (
													<TableRow key={idx}>
														<TableCell>{d.scratchCardName}</TableCell>
														<TableCell>{d.userName}</TableCell>
														<TableCell>{d.rewards}</TableCell>
														<TableCell>
															{d.redeemed?.toLowerCase() === "yes"
																? "Yes"
																: "No"}
														</TableCell>
														<TableCell>
															{formatCurrency(d.businessGenerated)}
														</TableCell>
														<TableCell>
															{d.issuedDate
																? getFormattedDateTime(d.issuedDate) || "-"
																: "-"}
														</TableCell>
														<TableCell>
															{d.redeemedDate
																? getFormattedDateTime(d.redeemedDate) || "-"
																: "-"}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>

										{scratchDetailsTotalRecords > 0 && (
											<div className="mt-4 flex justify-between items-center">
												<div className="flex items-center space-x-2">
													<span className="text-sm text-gray-700">Rows per page:</span>
													<Select
														value={scratchDetailsPageSize.toString()}
														onValueChange={(value) => {
															const newPageSize = parseInt(value, 10);
															setScratchDetailsPageSize(newPageSize);
															setScratchDetailsPage(0);
														}}
													>
														<SelectTrigger className="w-20">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="5">5</SelectItem>
															<SelectItem value="10">10</SelectItem>
															<SelectItem value="20">20</SelectItem>
															<SelectItem value="30">30</SelectItem>
															<SelectItem value="50">50</SelectItem>
														</SelectContent>
													</Select>
												</div>

												{Math.ceil(
													scratchDetailsTotalRecords / scratchDetailsPageSize
												) > 1 && (
													<PaginationControls
														currentPage={scratchDetailsPage}
														totalPages={Math.ceil(
															scratchDetailsTotalRecords / scratchDetailsPageSize
														)}
														onPrevHandler={() =>
															setScratchDetailsPage((p) =>
																Math.max(0, p - 1)
															)
														}
														onNextHandler={() =>
															setScratchDetailsPage((p) =>
																Math.min(
																	Math.ceil(
																		scratchDetailsTotalRecords / scratchDetailsPageSize
																	) - 1,
																	p + 1
																)
															)
														}
														onPageChange={(page) =>
															setScratchDetailsPage(page)
														}
													/>
												)}
											</div>
										)}
									</>
								)}
							</div>
						) : (
							<div className="space-y-4 py-2">
								{isSpinDetailsLoading ? (
									<CouponTableSkeleton rowCount={5} />
								) : spinDetails.length === 0 ? (
									<div className="text-sm text-gray-500 py-4">
										No detail records available for this report.
									</div>
								) : (
									<>
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>{primaryNameLabel}</TableHead>
													<TableHead>User name</TableHead>
													<TableHead>Rewards</TableHead>
													<TableHead>Redeemed</TableHead>
													<TableHead>Business Generated</TableHead>
													<TableHead>IssuedDate</TableHead>
													<TableHead>RedeemDate</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{spinDetails.map((d, idx) => (
													<TableRow key={idx}>
														<TableCell>{d.spinName}</TableCell>
														<TableCell>{d.userName}</TableCell>
														<TableCell>{d.rewards}</TableCell>
														<TableCell>
															{d.redeemed?.toLowerCase() === "yes"
																? "Yes"
																: "No"}
														</TableCell>
														<TableCell>
															{formatCurrency(d.businessGenerated)}
														</TableCell>
														<TableCell>
															{d.issuedDate
																? getFormattedDateTime(d.issuedDate) || "-"
																: "-"}
														</TableCell>
														<TableCell>
															{d.redeemedDate
																? getFormattedDateTime(d.redeemedDate) || "-"
																: "-"}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>

										{spinDetailsTotalRecords > 0 && (
											<div className="mt-4 flex justify-between items-center">
												<div className="flex items-center space-x-2">
													<span className="text-sm text-gray-700">
														Rows per page:
													</span>
													<Select
														value={spinDetailsPageSize.toString()}
														onValueChange={(value) => {
															const newPageSize = parseInt(
																value,
																10
															);
															setSpinDetailsPageSize(newPageSize);
															setSpinDetailsPage(0);
														}}
													>
														<SelectTrigger className="w-20">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="5">5</SelectItem>
															<SelectItem value="10">10</SelectItem>
															<SelectItem value="20">20</SelectItem>
															<SelectItem value="30">30</SelectItem>
															<SelectItem value="50">50</SelectItem>
														</SelectContent>
													</Select>
												</div>

												{Math.ceil(
													spinDetailsTotalRecords / spinDetailsPageSize
												) > 1 && (
													<PaginationControls
														currentPage={spinDetailsPage}
														totalPages={Math.ceil(
															spinDetailsTotalRecords / spinDetailsPageSize
														)}
														onPrevHandler={() =>
															setSpinDetailsPage((p) =>
																Math.max(0, p - 1)
															)
														}
														onNextHandler={() =>
															setSpinDetailsPage((p) =>
																Math.min(
																	Math.ceil(
																		spinDetailsTotalRecords / spinDetailsPageSize
																	) - 1,
																	p + 1
																)
															)
														}
														onPageChange={(page) =>
															setSpinDetailsPage(page)
														}
													/>
												)}
											</div>
										)}
									</>
								)}
							</div>
						))}

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								setIsDetailsOpen(false);
								setSelectedRow(null);
							}}
						>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

